import { useState } from "react";
import { useSignIn, useSignUp } from "@clerk/clerk-react";
import { Bubbles, Mascot } from "../components/Ambient";
import { Button } from "../components/Button";
import { Field } from "../components/Field";
import { Footer } from "../components/Footer";
import { NavBar } from "../components/NavBar";
import { Panel } from "../components/Panel";
import type { ToastTone } from "../components/contracts";

/**
 * Sign-in, built from Clerk's headless hooks rather than its drop-in widget.
 *
 * The prebuilt <SignIn/> renders Clerk's own markup, which cannot be made to
 * look like this app no matter how far the `appearance` prop is pushed — it
 * would read as someone else's modal bolted onto the sky.
 *
 * The flow is deliberately email → 6-digit code, with no password:
 *   - nothing to forget, nothing to leak, no reset flow to build
 *   - it mirrors the room code the game already asks people to type
 *   - it is one screen instead of four
 *
 * Sign-in and sign-up are the SAME journey. Asking someone whether they
 * already have an account is a question they often cannot answer; we try to
 * sign in, and fall back to creating the account when Clerk says there isn't
 * one.
 */
export interface SignInProps {
  onDone: () => void;
  onCancel: () => void;
  onToast: (text: string, tone?: ToastTone) => void;
}

type Stage = "email" | "code";

export function SignIn({ onDone, onCancel, onToast }: SignInProps) {
  const signInHook = useSignIn();
  const signUpHook = useSignUp();

  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Which flow the code belongs to — they have different verify calls. */
  const [mode, setMode] = useState<"signIn" | "signUp">("signIn");

  const ready = signInHook.isLoaded && signUpHook.isLoaded;
  const emailOk = /.+@.+\..+/.test(email.trim());
  const codeOk = code.trim().length === 6;

  /** Clerk errors arrive as a list; the first long message is the useful one. */
  function clerkMessage(err: unknown, fallback: string): string {
    const errors = (err as { errors?: Array<{ longMessage?: string; message?: string }> })
      ?.errors;
    const first = errors?.[0];
    return first?.longMessage || first?.message || fallback;
  }

  async function sendCode() {
    if (!ready || !emailOk || busy) return;
    setBusy(true);
    setError(null);
    const address = email.trim();

    try {
      // Try signing in first. An existing account is the common case for
      // everyone except a player's very first time.
      await signInHook.signIn!.create({ strategy: "email_code", identifier: address });
      setMode("signIn");
      setStage("code");
      onToast("Code sent. Check your email.", "success");
      return;
    } catch (signInErr) {
      // No such account — create one. Any other failure is a real error.
      const code0 = (signInErr as { errors?: Array<{ code?: string }> })?.errors?.[0]?.code;
      if (code0 !== "form_identifier_not_found") {
        setError(clerkMessage(signInErr, "Couldn't send a code to that address."));
        setBusy(false);
        return;
      }
    }

    try {
      await signUpHook.signUp!.create({ emailAddress: address });
      await signUpHook.signUp!.prepareEmailAddressVerification({ strategy: "email_code" });
      setMode("signUp");
      setStage("code");
      onToast("Code sent. Check your email.", "success");
    } catch (signUpErr) {
      setError(clerkMessage(signUpErr, "Couldn't create an account with that address."));
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (!ready || !codeOk || busy) return;
    setBusy(true);
    setError(null);
    const value = code.trim();

    try {
      if (mode === "signIn") {
        const result = await signInHook.signIn!.attemptFirstFactor({
          strategy: "email_code",
          code: value,
        });
        if (result.status !== "complete") throw new Error("That didn't complete.");
        await signInHook.setActive!({ session: result.createdSessionId });
      } else {
        const result = await signUpHook.signUp!.attemptEmailAddressVerification({
          code: value,
        });
        if (result.status !== "complete") throw new Error("That didn't complete.");
        await signUpHook.setActive!({ session: result.createdSessionId });
      }
      onToast("You're signed in.", "success");
      onDone();
    } catch (err) {
      setError(clerkMessage(err, "That code didn't work. Check it and try again."));
      setBusy(false);
    }
  }

  return (
    <div className="bd-screen">
      <Bubbles />
      <NavBar />

      <main className="shell bd-stack-lg">
        <div className="plate chamfer bd-hero bd-slide-in">
          <Mascot mood="idle" size={78} />
          <h1 className="bd-wordmark t-outlined" style={{ fontSize: "clamp(34px,10vw,52px)" }}>
            <span>{stage === "email" ? "Save your" : "Check your"}</span>
            <span>{stage === "email" ? "games" : "email"}</span>
          </h1>
          <p className="t-hero-tagline bd-hero-tag">
            {stage === "email"
              ? "Optional, always. An account keeps your scores, your history and who you played with. You never need one to play."
              : `We sent a 6-digit code to ${email.trim()}.`}
          </p>
        </div>

        <Panel title={stage === "email" ? "Your email" : "Your code"}>
          {stage === "email" ? (
            <>
              <Field
                label="Email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => e.key === "Enter" && void sendCode()}
                error={error}
                hint="No password. We email you a code instead."
              />
              <Button
                variant="submit"
                block
                beacon={emailOk && !busy}
                disabled={!ready || !emailOk || busy}
                onClick={() => void sendCode()}
              >
                {busy ? "Sending…" : "Email me a code"}
              </Button>
            </>
          ) : (
            <>
              <Field
                label="6-digit code"
                code
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="——————"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setError(null);
                }}
                onKeyDown={(e) => e.key === "Enter" && void verify()}
                error={error}
              />
              <Button
                variant="submit"
                block
                beacon={codeOk && !busy}
                disabled={!ready || !codeOk || busy}
                onClick={() => void verify()}
              >
                {busy ? "Checking…" : "Sign in"}
              </Button>
              <Button
                variant="secondary"
                block
                disabled={busy}
                onClick={() => {
                  setStage("email");
                  setCode("");
                  setError(null);
                }}
              >
                Use a different email
              </Button>
            </>
          )}

          <hr className="dotted-divider" />
          <Button variant="secondary" block onClick={onCancel}>
            Not now &mdash; keep playing without an account
          </Button>
        </Panel>
      </main>

      <Footer />
    </div>
  );
}
